/**
 * <mega-menu> - desktop kategori-flyout (BUILD_PLAN 2.3, NY JS #1).
 * Non-modal disclosure under headerens kategori-raekke: hover-intent aabner
 * panelet (kun paa hover:hover-enheder), og keyboard-FOKUS paa top-linket
 * aabner det samme (ingen chevron - designet). Top-linket forbliver et rigtigt
 * <a>: klik navigerer til kategorisiden, hover/fokus afsloerer panelet.
 * Escape lukker og returnerer fokus til top-linket; klik udenfor og focusout
 * lukker. Kun ét panel ad gangen.
 *
 * A11y: disclosure-moenster - JS saetter aria-expanded paa triggeren (holdes UDE
 * af markup, saa no-JS ikke lover en tilstand der ikke kan aendres); aria-
 * controls peger paa panelet fra markup. Panelerne er 100%
 * server-renderede (linklist-boern + tile-blocks) - ingen fetch, ingen penge/
 * locale i JS. Progressive enhancement: uden JS navigerer top-links som normale
 * kategorilinks (underkategorier findes ogsaa paa selve kategorisiden).
 */

const HOVER_OPEN_MS = 80;
const HOVER_CLOSE_MS = 150;
// Panel-lukkeanimationen (clip-path) varer --dur-panel. Headerens sorte flade
// holdes lige saa laenge (.is-closing), saa strippen ikke reverterer til lyst
// FOER panelet er klippet vaek. Spejler --dur-panel i css-variables.liquid.
const PANEL_MS = 280;

class MegaMenu extends HTMLElement {
  #open = null;
  #timer = null;
  #closeTimer = null;
  #closingItem = null;
  #escaping = false;
  #hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.scrim = this.querySelector('[data-mega-scrim]');
    // Scrim-tap lukker (dismiss-moensteret fra filter-sheetet): scrimmen bor
    // INDE i <mega-menu>, saa document-click-handleren ser den som "indenfor".
    if (this.scrim) this.scrim.addEventListener('click', () => this.#close());
    this.items = [...this.querySelectorAll('[data-mega-item]')];

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-mega-trigger]');
      // Disclosure-moenster (WAI): aria-expanded + aria-controls, IKKE
      // aria-haspopup (panelet er en gruppe links, ikke en ARIA-menu med
      // pil-navigation). aria-expanded saettes af JS, saa no-JS-markup ikke
      // lover en tilstand der ikke kan aendres.
      trigger.setAttribute('aria-expanded', 'false');
      // Keyboard: fokus paa top-linket aabner panelet (afloeser chevron-knappen).
      // Efter Escape returneres fokus hertil - #escaping undgaar at det saa
      // straks aabner igen.
      trigger.addEventListener('focus', () => {
        if (!this.#escaping) this.#show(item);
      });
      // Hover-intent: kort delay begge veje, saa diagonale mus-bevaegelser
      // ikke flimrer panelerne. Panelet ligger INDE i item'et, saa pointer-
      // leave daekker ogsaa "forlod panelet".
      item.addEventListener('pointerenter', () => {
        if (!this.#hoverable.matches) return;
        this.#schedule(() => this.#show(item), HOVER_OPEN_MS);
      });
      item.addEventListener('pointerleave', () => {
        if (!this.#hoverable.matches) return;
        this.#schedule(() => this.#close(), HOVER_CLOSE_MS);
      });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#open) return;
      const trigger = this.#open.querySelector('[data-mega-trigger]');
      // trigger.focus() er synkron -> flag om Escape saa focus-handleren ikke
      // genaabner panelet med det samme.
      this.#escaping = true;
      this.#close();
      trigger.focus();
      this.#escaping = false;
    });
    // Tab ud af hele komponenten lukker (ingen fokusfaelde - non-modal).
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#close();
    });

    this.onDocumentClick = (event) => {
      if (this.#open && !this.contains(event.target)) this.#close();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    clearTimeout(this.#closeTimer);
    document.removeEventListener('click', this.onDocumentClick);
  }

  #schedule(fn, ms) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(fn, ms);
  }

  #show(item) {
    if (this.#open === item) return;
    this.#endClosing();
    this.#setState(this.#open, false);
    this.#open = item;
    this.#setState(item, true);
  }

  #close() {
    clearTimeout(this.#timer);
    if (!this.#open) return;
    const closingItem = this.#open;
    this.#setState(closingItem, false); // fjerner .is-open -> panelet clip-lukker
    this.#open = null;
    // Hold header-dark mens panelet klipper vaek; ryd naar animationen er ovre
    // (0ms under reduced motion, hvor panelet lukker oejeblikkeligt).
    this.#endClosing();
    this.#closingItem = closingItem;
    closingItem.classList.add('is-closing');
    const hold = this.#reduced.matches ? 0 : PANEL_MS;
    this.#closeTimer = setTimeout(() => this.#endClosing(), hold);
  }

  #endClosing() {
    clearTimeout(this.#closeTimer);
    if (this.#closingItem) {
      this.#closingItem.classList.remove('is-closing');
      this.#closingItem = null;
    }
  }

  // Én kilde til sandhed: .is-open paa item'et. CSS driver panel+scrim+header-
  // token-flip fra den klasse (clip-path/visibility - animérbar lukket-tilstand,
  // aldrig display:none, saa reveal + reverse-luk kan tone). aria-expanded er
  // stadig JS-ejet (no-JS lover ingen tilstand). Panelet fjernes fra a11y-traeet
  // + tab-orden via visibility:hidden i CSS, som [hidden] gjorde foer.
  #setState(item, open) {
    if (!item) return;
    item.querySelector('[data-mega-trigger]').setAttribute('aria-expanded', String(open));
    item.classList.toggle('is-open', open);
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
